import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ProcessingStatus,
  RecommendationPlanIds,
  type ProfileBasedRecommendation,
  type useCompleteProfileBasedRecommendationParams,
} from './interfaces';
import { falconClient } from '@/data/@clients';
import { clientKeys } from '@/data/@clients/client-keys';
import { baseUrl } from '@/data/idp/base';

export const useCreateProfileBasedRecommendation = () => {
  return useMutation({
    mutationFn: async (
      current_step: RecommendationPlanIds
    ): Promise<ProfileBasedRecommendation> =>
      falconClient.post(`${baseUrl}/profile_based_recommendations`, {
        current_step,
      }),
  });
};

interface UseProfileBasedRecommendationShowParams {
  id: string;
  enabled?: boolean;
}

export const useProfileBasedRecommendationShow = ({
  id,
  enabled,
}: UseProfileBasedRecommendationShowParams) =>
  useQuery<ProfileBasedRecommendation>({
    queryFn: async () =>
      falconClient.get(`${baseUrl}/profile_based_recommendations/${id}`),
    queryKey: [clientKeys.useProfileBasedRecommendationShow, id],
    enabled: enabled ?? !!id,
  });

export const useUpdateProfileBasedRecommendation = (id: string) => {
  return useMutation({
    mutationFn: async (
      profileBasedRecommendation: Partial<ProfileBasedRecommendation>
    ) =>
      falconClient.patch(
        `${baseUrl}/profile_based_recommendations/${id}`,
        profileBasedRecommendation
      ),
  });
};

export const useDeleteProfileBasedRecommendation = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      return falconClient.delete(
        `${baseUrl}/profile_based_recommendations/${id}`
      );
    },
  });
};

const retryInterval = 5000;

export const useProfileBasedRecommendationPolling = (
  recommendationId: string
) => {
  let status: ProcessingStatus = ProcessingStatus.Pending;

  const { data, isPending, refetch } = useQuery<ProfileBasedRecommendation>({
    queryFn: async () =>
      falconClient.get(
        `${baseUrl}/profile_based_recommendations/${recommendationId}`
      ),
    queryKey: [
      clientKeys.useProfileBasedRecommendationPolling,
      recommendationId,
    ],
    refetchInterval(query) {
      if (
        query.state.data === null ||
        query.state.data?.processing_status === ProcessingStatus.Fail
      ) {
        return false;
      }

      return retryInterval;
    },
    enabled: !!recommendationId,
  });

  const recommendedActivities = data?.recommended_activities || null;
  const isGenerating = !recommendedActivities?.length;

  if (!isPending) {
    status = data?.processing_status ?? ProcessingStatus.Fail;
  }

  return { data, recommendedActivities, isGenerating, status, refetch };
};

export const useCompleteProfileBasedRecommendation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      activities,
      currentStep,
      id,
    }: useCompleteProfileBasedRecommendationParams): Promise<ProfileBasedRecommendation> => {
      return await falconClient.post(
        `${baseUrl}/profile_based_recommendations/${id}/complete`,
        { activities, current_step: currentStep }
      );
    },
    onSuccess: () => {
      queryClient.resetQueries({
        queryKey: [clientKeys.usePlansIndex],
      });
      queryClient.resetQueries({
        queryKey: [clientKeys.usePlanShow],
      });
    },
  });
};

export const useRedoRecommendation = () => {
  return useMutation({
    mutationFn: async (id: string): Promise<ProfileBasedRecommendation> => {
      await falconClient.delete(
        `${baseUrl}/profile_based_recommendations/${id}`
      );
      return await falconClient.post(
        `${baseUrl}/profile_based_recommendations`,
        {
          current_step: RecommendationPlanIds.Duration,
        }
      );
    },
  });
};
